import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, MapPin, Phone, Mail, Globe, ArrowLeft, Save, CheckCircle, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const Button = ({ children, variant = 'primary', icon: Icon, className = '', onClick, type = 'button', disabled = false }) => {
  const baseStyle = "inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  const variantStyles = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white shadow-sm shadow-blue-600/20 focus:ring-blue-500",
    secondary: "bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 shadow-sm focus:ring-gray-500",
    ghost: "text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:ring-gray-500",
  };
  return (
    <button type={type} className={`${baseStyle} ${variantStyles[variant]} ${className}`} onClick={onClick} disabled={disabled}>
      {Icon && <Icon className={`w-4 h-4 ${children ? 'mr-2' : ''}`} />}
      {children}
    </button>
  );
};

const Input = ({ label, type = 'text', name, value, onChange, placeholder, icon: Icon, required = false }) => {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Icon className="w-4 h-4" />
          </div>
        )}
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          className={`w-full ${Icon ? 'pl-10' : 'pl-3'} pr-3 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all`}
        />
      </div>
    </div>
  );
};

function ProfilePage() {
  const [profil, setProfil] = useState({
    raison_sociale: '',
    adresse_voirie: '',
    adresse_code_postal: '',
    adresse_ville: '',
    telephone: '',
    fax: '',
    email: '',
    web: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/profile`)
      .then(response => {
        setProfil(response.data);
        setLoading(false);
      })
      .catch(error => {
        setError('Erreur lors du chargement du profil.');
        setLoading(false);
      });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfil(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/profile`, profil);
      setProfil(response.data);
      setSuccess('Profil mis à jour avec succès!');
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      setError('Erreur lors de la mise à jour du profil.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4">
          <ArrowLeft className="w-4 h-4" />
          Retour aux annonces
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Profil de l'entreprise</h1>
        <p className="text-gray-500 mt-1">Gérez les informations de votre agence immobilière.</p>
      </div>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Chargement du profil...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Messages */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700"
            >
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span>{success}</span>
            </motion.div>
          )}

          {/* Informations générales */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-gray-200 shadow-sm p-6"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Informations générales
            </h2>

            <div className="space-y-4">
              <Input
                label="Raison sociale"
                name="raison_sociale"
                value={profil.raison_sociale}
                onChange={handleChange}
                icon={Building2}
                placeholder="Nom de l'entreprise"
                required
              />
            </div>
          </motion.div>

          {/* Adresse */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl border border-gray-200 shadow-sm p-6"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Adresse
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Input
                  label="Voirie"
                  name="adresse_voirie"
                  value={profil.adresse_voirie}
                  onChange={handleChange}
                  placeholder="Numéro et nom de rue"
                  required
                />
              </div>
              <Input
                label="Code postal"
                name="adresse_code_postal"
                value={profil.adresse_code_postal}
                onChange={handleChange}
                placeholder="30000"
                required
              />
              <Input
                label="Ville"
                name="adresse_ville"
                value={profil.adresse_ville}
                onChange={handleChange}
                icon={MapPin}
                placeholder="Nîmes"
                required
              />
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border border-gray-200 shadow-sm p-6"
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-600" />
              Contact
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Téléphone"
                name="telephone"
                value={profil.telephone}
                onChange={handleChange}
                icon={Phone}
                placeholder="04 66 00 00 00"
                required
              />
              <Input
                label="Fax"
                name="fax"
                value={profil.fax}
                onChange={handleChange}
                placeholder="04 66 00 00 01"
              />
              <Input
                label="Email"
                type="email"
                name="email"
                value={profil.email}
                onChange={handleChange}
                icon={Mail}
                placeholder="contact@villasgaia.fr"
                required
              />
              <Input
                label="Site web"
                name="web"
                value={profil.web}
                onChange={handleChange}
                icon={Globe}
                placeholder="https://www.villasgaia.fr"
                required
              />
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex items-center justify-end gap-3"
          >
            <Link to="/">
              <Button variant="secondary">Annuler</Button>
            </Link>
            <Button type="submit" icon={Save} disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
            </Button>
          </motion.div>
        </form>
      )}
    </div>
  );
}

export default ProfilePage;
